/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useMemo, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { isArray } from 'lodash';
import {
    Box,
    Typography,
    TextField,
    InputAdornment,
    Chip,
    alpha,
    IconButton,
    Popover,
    List,
    ListItemButton,
    ListItemText,
    Checkbox,
    Divider,
    Badge,
    Tooltip,
} from '@mui/material';
import { DataGridPro } from '@mui/x-data-grid-pro';
import { Iconify } from 'src/components';
import { useSelector as useReduxSelector, dispatch as reduxDispatch } from 'src/redux/store';
import {
    getTablePreferences,
    handleTablePreferences,
    handleTablePreferencesEdit,
    setTablePreferenceLoading,
} from 'src/redux/slices/tablePreferences';
import { getCurrentTableData } from 'src/utils/tablePreferences';
import useGetCurrentUserId from 'src/hooks/useGetCurrentUserId';
import useReportExportFilename from 'src/hooks/useReportExportFilename';
import SourceLeadPopup from './SourceLeadPopup';
import { PRACTICE_COMPARISON_GRID_SX } from './practiceGridSx';
import PracticeComparisonRateDialog from './PracticeComparisonRateDialog';
import {
    buildPracticeComparisonRow,
    formatPracticeComparisonRate,
    PRACTICE_COMPARISON_RATE_METRICS,
} from '../practiceComparisonMetrics';

const TABLE_NAME = 'Practice Comparison';

const ALL_COLUMNS = [
    { field: 'rank', label: '#' },
    { field: 'practiceName', label: 'Practice' },
    { field: 'totalLeads', label: 'Total Leads' },
    { field: 'bookedAppointments', label: 'Booked' },
    { field: 'attendedConsultations', label: 'Attended' },
    { field: 'startedTreatments', label: 'Started' },
    { field: 'bookingRate', label: 'Booked / Leads' },
    { field: 'showUpRate', label: 'Attended / Booked' },
    { field: 'conversionRate', label: 'Started / Attended' },
    { field: 'ftaRate', label: 'FTA Rate' },
    { field: 'overallConversion', label: 'Overall Conversion' },
];

// Column configuration for consistent Chip styling
const COLUMN_CONFIG = {
    totalLeads: { color: '#3b82f6' },
    bookedAppointments: { color: '#8b5cf6' },
    attendedConsultations: { color: '#6366f1' },
    startedTreatments: { color: '#10b981' },
    conversionRate: { good: '#10b981', medium: '#f59e0b', low: '#ef4444' },
};

// Per-column popup metadata, co-located so the three facts about a column stay
// in sync: the lead `status` to filter by, the human `label`, and the date
// `basis` — which MUST match how that column's COUNT is fetched in
// allPracticeReports.js. Total Leads counts leads CREATED in range
// (created_at__gte/lte) so it drills down on created_at; the status columns are
// counted via state_transition_params, so they drill down on that basis.
const COLUMN_META = {
    totalLeads: { status: 'All', label: 'New Enquiries', basis: 'created_at' },
    bookedAppointments: { status: 'CONSULTATION_BOOKED', label: 'Consultation Booked', basis: 'state_transition' },
    attendedConsultations: { status: 'CONSULTATION_ATTENDED', label: 'Consultation Attended', basis: 'state_transition' },
    startedTreatments: { status: 'TREATMENT_STARTED', label: 'Treatment Started', basis: 'state_transition' },
};

export default function PracticeComparisonTable({ data, dateRange }) {
    const [searchQuery, setSearchQuery] = useState('');
    const [sortModel, setSortModel] = useState([{ field: 'totalLeads', sort: 'desc' }]);
    const [columnsAnchor, setColumnsAnchor] = useState(null);
    const [exportAnchor, setExportAnchor] = useState(null);
    const [hiddenColumns, setHiddenColumns] = useState([]);
    const [prefsLoaded, setPrefsLoaded] = useState(false);
    const [rateDialog, setRateDialog] = useState({ open: false, row: null, metric: null });

    // Popup state
    const [popupOpen, setPopupOpen] = useState(false);
    const [popupConfig, setPopupConfig] = useState({
        practiceId: null,
        status: 'All',
        statusLabel: '',
        title: '',
        dateFilterMode: 'state_transition',
    });

    const handleCellClick = useCallback((row, field) => {
        const value = row[field];
        if (!value || value <= 0 || !row.id) return;
        const meta = COLUMN_META[field];
        if (!meta) return;
        setPopupConfig({
            practiceId: row.id,
            status: meta.status,
            statusLabel: meta.label,
            title: `${row.practiceName} — ${meta.label}`,
            dateFilterMode: meta.basis,
        });
        setPopupOpen(true);
    }, []);

    const handleClosePopup = useCallback(() => {
        setPopupOpen(false);
    }, []);

    // Table preferences from Redux
    const userId = useGetCurrentUserId();
    const buildExportName = useReportExportFilename();
    const { tablePreferences: tablePrefsRaw, globalTablePreferences } = useReduxSelector((state) => state.tablePreferences);
    const prefResults = tablePrefsRaw?.results || tablePrefsRaw || [];

    // Load user preferences on mount
    useEffect(() => {
        if (userId) {
            reduxDispatch(getTablePreferences({ practiceUserId: userId }));
        }
    }, [userId]);

    // Save preferences to API
    const savePreferences = useCallback((newHidden) => {
        if (!userId) return;
        reduxDispatch(setTablePreferenceLoading(true));
        const tableData = getCurrentTableData(prefResults, TABLE_NAME);
        const globalData = getCurrentTableData(globalTablePreferences?.results || [], TABLE_NAME);
        const currentTableData = isArray(tableData) && tableData.length > 0 ? tableData : globalData;
        const currentPreferences = currentTableData?.[0]?.preferences || {};
        const updatedPrefs = { ...currentPreferences, hiddenColumns: newHidden };
        const payload = {
            body: { table_name: TABLE_NAME, preferences: updatedPrefs },
            practiceUserId: userId,
        };
        if (isArray(tableData) && tableData.length > 0) {
            reduxDispatch(handleTablePreferencesEdit({ ...payload, tableId: tableData[0].id }));
        } else {
            reduxDispatch(handleTablePreferences(payload));
        }
    }, [userId, prefResults, globalTablePreferences]);

    // Load saved preferences
    useEffect(() => {
        if (!prefsLoaded && userId) {
            const tableData = getCurrentTableData(prefResults, TABLE_NAME);
            const globalData = getCurrentTableData(globalTablePreferences?.results || [], TABLE_NAME);
            const currentTableData = isArray(tableData) && tableData.length > 0 ? tableData : globalData;
            const prefs = currentTableData?.[0]?.preferences;
            if (prefs?.hiddenColumns && isArray(prefs.hiddenColumns)) {
                setHiddenColumns(prefs.hiddenColumns);
            }
            setPrefsLoaded(true);
        }
    }, [prefResults, globalTablePreferences, userId]);

    // Column visibility model for DataGrid
    const columnVisibilityModel = useMemo(() => {
        const model = {};
        hiddenColumns.forEach((field) => { model[field] = false; });
        return model;
    }, [hiddenColumns]);

    // Whether filter is active
    const isFilterActive = hiddenColumns.length > 0;

    // Toggle column visibility
    const handleToggleColumn = useCallback((field) => {
        const updated = hiddenColumns.includes(field)
            ? hiddenColumns.filter((f) => f !== field)
            : [...hiddenColumns, field];
        setHiddenColumns(updated);
        savePreferences(updated);
    }, [hiddenColumns, savePreferences]);

    // Process data with calculated fields
    const processedData = useMemo(() => {
        if (!data?.length) return [];

        return data.map(buildPracticeComparisonRow);
    }, [data]);

    // Export CSV
    const handleExportCSV = useCallback(() => {
        setExportAnchor(null);
        if (!processedData || processedData.length === 0) return;
        const visibleCols = ALL_COLUMNS.filter((c) => !hiddenColumns.includes(c.field));
        const headers = visibleCols.map((c) => c.label);
        const rateFields = ['bookingRate', 'showUpRate', 'conversionRate', 'ftaRate', 'overallConversion'];
        const rows = processedData.map((p) => visibleCols.map((col) => {
            if (rateFields.includes(col.field)) {
                return formatPracticeComparisonRate(p[col.field]);
            }
            return p[col.field] ?? '-';
        }));
        const csvContent = [headers, ...rows].map((row) => row.map((cell) => `"${cell}"`).join(',')).join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = buildExportName('practice-comparison', dateRange, { practiceName: 'all-practices' });
        link.click();
        URL.revokeObjectURL(link.href);
    }, [processedData, hiddenColumns, buildExportName, dateRange]);

    // Filter data based on search
    const filteredData = useMemo(() => {
        if (!searchQuery) return processedData;
        return processedData.filter((row) =>
            row.practiceName?.toLowerCase().includes(searchQuery.toLowerCase())
        );
    }, [processedData, searchQuery]);

    // Calculate averages for comparison
    const averages = useMemo(() => {
        if (!processedData?.length) return { leads: 0, conversion: 0 };
        const total = processedData.reduce(
            (acc, item) => ({
                leads: acc.leads + (item.totalLeads || 0),
                conversion: acc.conversion + (item.conversionRate || 0),
            }),
            { leads: 0, conversion: 0 }
        );
        return {
            leads: total.leads / processedData.length,
            conversion: total.conversion / processedData.length,
        };
    }, [processedData]);

    const columns = useMemo(() => [
        {
            field: 'rank',
            headerName: '#',
            flex: 0.4,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => (
                <Typography sx={{ fontWeight: 600, fontSize: '14px', color: '#64748b' }}>
                    {params.value}
                </Typography>
            ),
        },
        {
            field: 'practiceName',
            headerName: 'Practice',
            flex: 1.5,
            renderCell: (params) => (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pl: 1 }}>
                    <Box
                        sx={{
                            width: 32,
                            height: 32,
                            borderRadius: '8px',
                            bgcolor: '#e0f2fe',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontWeight: 600,
                            fontSize: '12px',
                            color: '#0369a1',
                        }}
                    >
                        {params.value?.charAt(0)?.toUpperCase() || 'P'}
                    </Box>
                    <Typography sx={{ fontWeight: 500, fontSize: '14px' }}>
                        {params.value}
                    </Typography>
                </Box>
            ),
        },
        {
            field: 'totalLeads',
            headerName: 'Total Leads',
            flex: 1,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => {
                const value = params.value || 0;
                const config = COLUMN_CONFIG.totalLeads;
                const isAboveAvg = value > averages.leads * 1.1;
                const isBelowAvg = value < averages.leads * 0.9;
                return (
                    <Tooltip title={`${value} total new enquiries received`} arrow placement="top">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: value > 0 ? 'pointer' : 'help' }}>
                            <Chip
                                label={value}
                                size="small"
                                onClick={value > 0 ? (e) => { e.stopPropagation(); handleCellClick(params.row, 'totalLeads'); } : undefined}
                                sx={{
                                    bgcolor: value > 0 ? alpha(config.color, 0.12) : 'transparent',
                                    color: value > 0 ? config.color : 'text.disabled',
                                    fontWeight: 600,
                                    fontSize: '12px',
                                    height: 26,
                                    minWidth: 40,
                                    cursor: value > 0 ? 'pointer' : 'default',
                                    '&:hover': value > 0 ? { bgcolor: alpha(config.color, 0.22) } : {},
                                }}
                            />
                            {isAboveAvg && (
                                <Iconify icon="mdi:trending-up" width={16} sx={{ color: '#10b981' }} />
                            )}
                            {isBelowAvg && (
                                <Iconify icon="mdi:trending-down" width={16} sx={{ color: '#ef4444' }} />
                            )}
                        </Box>
                    </Tooltip>
                );
            },
        },
        {
            field: 'bookedAppointments',
            headerName: 'Booked',
            flex: 1,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => {
                const value = params.value || 0;
                const config = COLUMN_CONFIG.bookedAppointments;
                return (
                    <Tooltip title={`${value} consultations booked`} arrow placement="top">
                        <Box sx={{ cursor: value > 0 ? 'pointer' : 'help' }}>
                            <Chip
                                label={value}
                                size="small"
                                onClick={value > 0 ? (e) => { e.stopPropagation(); handleCellClick(params.row, 'bookedAppointments'); } : undefined}
                                sx={{
                                    bgcolor: value > 0 ? alpha(config.color, 0.12) : 'transparent',
                                    color: value > 0 ? config.color : 'text.disabled',
                                    fontWeight: 600,
                                    fontSize: '12px',
                                    height: 26,
                                    minWidth: 40,
                                    cursor: value > 0 ? 'pointer' : 'default',
                                    '&:hover': value > 0 ? { bgcolor: alpha(config.color, 0.22) } : {},
                                }}
                            />
                        </Box>
                    </Tooltip>
                );
            },
        },
        {
            field: 'attendedConsultations',
            headerName: 'Attended',
            flex: 1,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => {
                const value = params.value || 0;
                const config = COLUMN_CONFIG.attendedConsultations;
                return (
                    <Tooltip title={`${value} consultations attended`} arrow placement="top">
                        <Box sx={{ cursor: value > 0 ? 'pointer' : 'help' }}>
                            <Chip
                                label={value}
                                size="small"
                                onClick={value > 0 ? (e) => { e.stopPropagation(); handleCellClick(params.row, 'attendedConsultations'); } : undefined}
                                sx={{
                                    bgcolor: value > 0 ? alpha(config.color, 0.12) : 'transparent',
                                    color: value > 0 ? config.color : 'text.disabled',
                                    fontWeight: 600,
                                    fontSize: '12px',
                                    height: 26,
                                    minWidth: 40,
                                    cursor: value > 0 ? 'pointer' : 'default',
                                    '&:hover': value > 0 ? { bgcolor: alpha(config.color, 0.22) } : {},
                                }}
                            />
                        </Box>
                    </Tooltip>
                );
            },
        },
        {
            field: 'startedTreatments',
            headerName: 'Started',
            flex: 1,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => {
                const value = params.value || 0;
                const config = COLUMN_CONFIG.startedTreatments;
                return (
                    <Tooltip title={`${value} treatments started`} arrow placement="top">
                        <Box sx={{ cursor: value > 0 ? 'pointer' : 'help' }}>
                            <Chip
                                label={value}
                                size="small"
                                onClick={value > 0 ? (e) => { e.stopPropagation(); handleCellClick(params.row, 'startedTreatments'); } : undefined}
                                sx={{
                                    bgcolor: value > 0 ? alpha(config.color, 0.12) : 'transparent',
                                    color: value > 0 ? config.color : 'text.disabled',
                                    fontWeight: 600,
                                    fontSize: '12px',
                                    height: 26,
                                    minWidth: 40,
                                    cursor: value > 0 ? 'pointer' : 'default',
                                    '&:hover': value > 0 ? { bgcolor: alpha(config.color, 0.22) } : {},
                                }}
                            />
                        </Box>
                    </Tooltip>
                );
            },
        },
        {
            field: 'bookingRate',
            headerName: 'Booked / Leads',
            flex: 1,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => {
                const rate = params.value || 0;
                const { bookedAppointments, totalLeads } = params.row;
                const getRateColor = (r) => {
                    if (r >= 60) return COLUMN_CONFIG.conversionRate.good;
                    if (r >= 40) return COLUMN_CONFIG.conversionRate.medium;
                    return COLUMN_CONFIG.conversionRate.low;
                };
                const color = getRateColor(rate);
                return (
                    <Tooltip title={`${bookedAppointments} booked out of ${totalLeads} leads`} arrow placement="top">
                        <Box sx={{ cursor: 'help' }}>
                            <Chip
                                label={formatPracticeComparisonRate(params.value)}
                                size="small"
                                sx={{
                                    bgcolor: alpha(color, 0.12),
                                    color,
                                    fontWeight: 600,
                                    fontSize: '12px',
                                    height: 26,
                                    minWidth: 50,
                                }}
                            />
                        </Box>
                    </Tooltip>
                );
            },
        },
        {
            field: 'showUpRate',
            headerName: 'Attended / Booked',
            flex: 1,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => {
                const rate = params.value || 0;
                const { attendedConsultations, bookedAppointments } = params.row;
                const getRateColor = (r) => {
                    if (r >= 80) return COLUMN_CONFIG.conversionRate.good;
                    if (r >= 60) return COLUMN_CONFIG.conversionRate.medium;
                    return COLUMN_CONFIG.conversionRate.low;
                };
                const color = getRateColor(rate);
                return (
                    <Tooltip title={`${attendedConsultations} attended out of ${bookedAppointments} booked`} arrow placement="top">
                        <Box sx={{ cursor: 'help' }}>
                            <Chip
                                label={formatPracticeComparisonRate(params.value)}
                                size="small"
                                sx={{
                                    bgcolor: alpha(color, 0.12),
                                    color,
                                    fontWeight: 600,
                                    fontSize: '12px',
                                    height: 26,
                                    minWidth: 50,
                                }}
                            />
                        </Box>
                    </Tooltip>
                );
            },
        },
        {
            field: 'conversionRate',
            headerName: 'Started / Attended',
            flex: 1,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => {
                const rate = params.value || 0;
                const { startedTreatments, attendedConsultations } = params.row;
                const isAboveAvg = rate >= averages.conversion;
                const color = isAboveAvg ? COLUMN_CONFIG.conversionRate.good : COLUMN_CONFIG.conversionRate.medium;
                return (
                    <Tooltip title={`${startedTreatments} started out of ${attendedConsultations} attended`} arrow placement="top">
                        <Box sx={{ cursor: 'help' }}>
                            <Chip
                                label={formatPracticeComparisonRate(params.value)}
                                size="small"
                                sx={{
                                    bgcolor: alpha(color, 0.12),
                                    color,
                                    fontWeight: 600,
                                    fontSize: '12px',
                                    height: 26,
                                    minWidth: 50,
                                }}
                            />
                        </Box>
                    </Tooltip>
                );
            },
        },
        {
            field: 'ftaRate',
            headerName: 'FTA Rate',
            flex: 1,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => {
                const rate = params.value || 0;
                const fta = params.row.rateDetails?.ftaRate?.numerator?.count || 0;
                const resolved = params.row.rateDetails?.ftaRate?.denominator?.count || 0;
                // FTA: lower is better. <10% good, 10-20% medium, >20% bad
                const getFtaColor = (r) => {
                    if (r <= 10) return COLUMN_CONFIG.conversionRate.good;
                    if (r <= 20) return COLUMN_CONFIG.conversionRate.medium;
                    return COLUMN_CONFIG.conversionRate.low;
                };
                const color = getFtaColor(rate);
                return (
                    <Tooltip title={`${fta} failed to attend out of ${resolved} resolved consultations`} arrow placement="top">
                        <Box sx={{ cursor: 'help' }}>
                            <Chip
                                label={formatPracticeComparisonRate(params.value)}
                                size="small"
                                sx={{
                                    bgcolor: alpha(color, 0.12),
                                    color,
                                    fontWeight: 600,
                                    fontSize: '12px',
                                    height: 26,
                                    minWidth: 50,
                                }}
                            />
                        </Box>
                    </Tooltip>
                );
            },
        },
        {
            field: 'overallConversion',
            headerName: 'Overall Conversion',
            flex: 1,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => {
                const rate = params.value || 0;
                const { startedTreatments, totalLeads } = params.row;
                // Overall: started/leads. 25-40% is benchmark
                const getOverallColor = (r) => {
                    if (r >= 25) return COLUMN_CONFIG.conversionRate.good;
                    if (r >= 15) return COLUMN_CONFIG.conversionRate.medium;
                    return COLUMN_CONFIG.conversionRate.low;
                };
                const color = getOverallColor(rate);
                return (
                    <Tooltip title={`${startedTreatments} started out of ${totalLeads} total leads`} arrow placement="top">
                        <Box sx={{ cursor: 'help' }}>
                            <Chip
                                label={formatPracticeComparisonRate(params.value)}
                                size="small"
                                sx={{
                                    bgcolor: alpha(color, 0.12),
                                    color,
                                    fontWeight: 600,
                                    fontSize: '12px',
                                    height: 26,
                                    minWidth: 50,
                                }}
                            />
                        </Box>
                    </Tooltip>
                );
            },
        },
    ], [averages, handleCellClick]);

    return (
        <Box
            sx={{
                bgcolor: '#fff',
                borderRadius: '16px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                overflow: 'hidden',
                mb: 3,
            }}
        >
            {/* Header with Search and Toolbar */}
            <Box
                sx={{
                    p: 2.5,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: 2,
                }}
            >
                <Box>
                    <Typography sx={{ fontSize: '20px', fontWeight: 700 }}>
                        Practice Comparison
                    </Typography>
                    <Typography data-testid="practice-comparison-basis" sx={{ fontSize: '12px', color: '#64748b' }}>
                        Leads created in this period, by furthest stage reached
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <TextField
                        size="small"
                        placeholder="Search practice..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        sx={{ width: 250 }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Iconify icon="eva:search-outline" width={20} sx={{ color: 'text.disabled' }} />
                                </InputAdornment>
                            ),
                        }}
                    />

                    <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

                    {/* Column visibility */}
                    <Tooltip title="Show/hide columns" arrow>
                        <Badge
                            variant="dot"
                            invisible={!isFilterActive}
                            sx={{
                                '& .MuiBadge-badge': {
                                    bgcolor: '#3b82f6',
                                    width: 8,
                                    height: 8,
                                    minWidth: 8,
                                    top: 4,
                                    right: 4,
                                },
                            }}
                        >
                            <IconButton
                                size="small"
                                onClick={(e) => setColumnsAnchor(e.currentTarget)}
                                sx={{
                                    width: 32,
                                    height: 32,
                                    border: '1px solid',
                                    borderColor: (isFilterActive || columnsAnchor) ? 'primary.main' : '#e2e8f0',
                                    borderRadius: '8px',
                                    bgcolor: columnsAnchor ? alpha('#3b82f6', 0.08) : 'transparent',
                                    '&:hover': { bgcolor: alpha('#3b82f6', 0.08) },
                                }}
                            >
                                <Iconify icon="eva:options-2-outline" width={16} sx={{ color: (isFilterActive || columnsAnchor) ? 'primary.main' : '#64748b' }} />
                            </IconButton>
                        </Badge>
                    </Tooltip>

                    {/* Export */}
                    <Tooltip title="Export data" arrow>
                        <IconButton
                            size="small"
                            onClick={(e) => setExportAnchor(e.currentTarget)}
                            sx={{
                                width: 32,
                                height: 32,
                                border: '1px solid',
                                borderColor: exportAnchor ? 'primary.main' : '#e2e8f0',
                                borderRadius: '8px',
                                bgcolor: exportAnchor ? alpha('#3b82f6', 0.08) : 'transparent',
                                '&:hover': { bgcolor: alpha('#3b82f6', 0.08) },
                            }}
                        >
                            <Iconify icon="eva:download-outline" width={16} sx={{ color: exportAnchor ? 'primary.main' : '#64748b' }} />
                        </IconButton>
                    </Tooltip>

                    {/* Column visibility popover */}
                    <Popover
                        open={Boolean(columnsAnchor)}
                        anchorEl={columnsAnchor}
                        onClose={() => setColumnsAnchor(null)}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                        slotProps={{ paper: { sx: { mt: 1, borderRadius: '12px', boxShadow: '0 4px 20px rgba(0,0,0,0.12)', width: 240, maxHeight: 400 } } }}
                    >
                        <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid #f1f5f9' }}>
                            <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#1e293b' }}>
                                Columns
                            </Typography>
                        </Box>
                        <List dense sx={{ py: 0.5 }}>
                            {ALL_COLUMNS.map((col) => {
                                const isVisible = !hiddenColumns.includes(col.field);
                                return (
                                    <ListItemButton
                                        key={col.field}
                                        onClick={() => handleToggleColumn(col.field)}
                                        sx={{ py: 0.25, px: 1.5, opacity: isVisible ? 1 : 0.6 }}
                                    >
                                        <Checkbox
                                            size="small"
                                            checked={isVisible}
                                            sx={{ p: 0, mr: 1 }}
                                        />
                                        <ListItemText
                                            primary={col.label}
                                            primaryTypographyProps={{ fontSize: '13px', color: isVisible ? '#1e293b' : '#94a3b8' }}
                                        />
                                    </ListItemButton>
                                );
                            })}
                        </List>
                    </Popover>

                    {/* Export popover */}
                    <Popover
                        open={Boolean(exportAnchor)}
                        anchorEl={exportAnchor}
                        onClose={() => setExportAnchor(null)}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                        slotProps={{ paper: { sx: { mt: 1, borderRadius: '12px', boxShadow: '0 4px 20px rgba(0,0,0,0.12)', minWidth: 200 } } }}
                    >
                        <List dense sx={{ py: 0.5 }}>
                            <ListItemButton onClick={handleExportCSV} sx={{ py: 1, px: 2 }}>
                                <Iconify icon="mdi:file-delimited-outline" width={18} sx={{ color: '#64748b', mr: 1.5 }} />
                                <ListItemText primary="Export CSV" primaryTypographyProps={{ fontSize: '13px' }} />
                            </ListItemButton>
                        </List>
                    </Popover>
                </Box>
            </Box>

            {/* DataGrid */}
            <Box>
                <DataGridPro
                    rows={filteredData}
                    columns={columns}
                    sortModel={sortModel}
                    onSortModelChange={setSortModel}
                    columnVisibilityModel={columnVisibilityModel}
                    autoHeight
                    disableRowSelectionOnClick
                    onCellClick={(params) => {
                        if (!PRACTICE_COMPARISON_RATE_METRICS[params.field]) return;
                        setRateDialog({ open: true, row: params.row, metric: params.field });
                    }}
                    disableColumnMenu={false}
                    pagination
                    pageSizeOptions={[5, 10, 25]}
                    initialState={{
                        pagination: { paginationModel: { pageSize: 10 } },
                    }}
                    sx={PRACTICE_COMPARISON_GRID_SX}
                />
            </Box>

            {/* Footer with average */}
            {filteredData.length > 0 && (
                <Box
                    sx={{
                        p: 2,
                        bgcolor: '#f8fafc',
                        borderTop: '1px solid #e2e8f0',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                    }}
                >
                    <Typography variant="body2" color="textSecondary">
                        Showing {filteredData.length} practice{filteredData.length !== 1 ? 's' : ''}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                        Avg. Conversion: {averages.conversion.toFixed(1)}%
                    </Typography>
                </Box>
            )}

            {filteredData.length === 0 && (
                <Box sx={{ textAlign: 'center', py: 6 }}>
                    <Typography sx={{ color: '#64748b' }}>
                        {searchQuery ? 'No practices match your search' : 'Select practices to compare'}
                    </Typography>
                </Box>
            )}

            {/* Lead detail popup */}
            <SourceLeadPopup
                open={popupOpen}
                onClose={handleClosePopup}
                practiceId={popupConfig.practiceId}
                status={popupConfig.status}
                statusLabel={popupConfig.statusLabel}
                dateRange={dateRange}
                title={popupConfig.title}
                dateFilterMode={popupConfig.dateFilterMode}
            />
            <PracticeComparisonRateDialog
                open={rateDialog.open}
                onClose={() => setRateDialog({ open: false, row: null, metric: null })}
                row={rateDialog.row}
                metric={rateDialog.metric}
            />
        </Box>
    );
}

PracticeComparisonTable.propTypes = {
    data: PropTypes.arrayOf(PropTypes.object),
    dateRange: PropTypes.shape({
        startDate: PropTypes.instanceOf(Date),
        endDate: PropTypes.instanceOf(Date),
    }),
};

PracticeComparisonTable.defaultProps = {
    data: [],
    dateRange: null,
};
